"use client";

import { useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { call, client, setUnauthorizedHandler } from "./api";
import type { User } from "./types";

type Ctx = {
  user: User;
  isAdmin: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<Ctx | null>(null);

/** Loads the signed-in user and redirects to /login when there is none. */
export function AuthGate({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);

  const refresh = useCallback(async () => {
    setUser(await call(client.GET("/api/auth/me")));
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => router.replace("/login"));
    refresh().catch(() => router.replace("/login"));
    return () => setUnauthorizedHandler(null);
  }, [refresh, router]);

  const logout = useCallback(async () => {
    await client.POST("/api/auth/logout");
    setUser(null);
    router.replace("/login");
  }, [router]);

  const value = useMemo(
    () => (user ? { user, isAdmin: user.role === "admin", logout, refresh } : null),
    [user, logout, refresh],
  );
  if (!value) return <>{fallback}</>;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthGate>");
  return ctx;
}
